import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BasedMiniReel} from "../reels/BasedMiniReel";
import {DecadenceStatics} from "../config/DecadenceStatics";
import {Decadence} from "../Decadence";
import {DecadenceSymbol} from "../symbols/DecadenceSymbol";
import {DecadenceSound} from "../sound/DecadenceSound";
import {gsap} from "gsap"
import {DecadenceGameAssets} from "../DecadenceGameAssets";
import {DecadenceReelStopFlow} from "../gameflow/DecadenceReelStopFlow";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";
import {DecadenceReelSelectView} from "./DecadenceReelSelectView";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {LoopableList} from "../utils/LoopableList";

export class DecadenceReelsView extends PIXI.Container implements StageLayer {
    private boardAllowed:boolean = true;

    private numberOfActiveReels:number;
    private reelModeTransitionTL = gsap.timeline();
    public reelModels:BasedMiniReel[] = [];

    private tvSpine:PIXI.spine.Spine;
    private monsterSpine:PIXI.spine.Spine;
    private linkedWinsSpine:PIXI.spine.Spine;
    private lockedReelSpine:PIXI.spine.Spine;

    private reelMode1Positions:PIXI.Point[];
    private reelMode2Positions:PIXI.Point[];
    private reelMode4Positions:PIXI.Point[];

    private lockedReelMode1Positions:PIXI.Point[];
    private lockedReelMode2Positions:PIXI.Point[];
    private lockedReelMode4Positions:PIXI.Point[];

    public lockedSymbols:DecadenceSymbol[] = [];
    private lockedReelContainer = new PIXI.Container;

    private monsterWinSounds:LoopableList<string>;
    private monsterBigWinSounds:LoopableList<string>;

    constructor() {
        super();
        this.name = "ReelsView";

        const halfSymbolWidth = (DecadenceStatics.SYMBOL_WIDTH/2) + 30;
        const halfSymbolHeight = (DecadenceStatics.SYMBOL_HEIGHT/2) - 30;

        this.reelMode1Positions = [new PIXI.Point(360, 505)];

        this.reelMode2Positions = [
            new PIXI.Point(360, 505 - 50),
            new PIXI.Point(360, 505 + 70)
        ];

        this.boardAllowed = !Decadence.apiPlugin.gameClientConfiguration.replaceWincapInfo;

        this.reelMode4Positions = [
            new PIXI.Point(360 - halfSymbolWidth, 510 - 60),
            new PIXI.Point(360 - halfSymbolWidth, 510 + 60),
            new PIXI.Point(360 + halfSymbolWidth, 510 - 60),
            new PIXI.Point(360 + halfSymbolWidth, 510 + 60),
        ]

        for(let i = 0; i < 4; i++) {
            const reel = new BasedMiniReel(i, 1);
            reel.position.copyFrom(this.reelMode1Positions[0]);
            reel.reelSet = DecadenceStatics.DEFAULT_REEL_SET;
            reel.insertSymbols([new DecadenceSymbol("0", 0, 0)])
            this.reelModels.push(reel);
        }


        this.monsterSpine = DecadenceGameAssets.createSpine(DecadenceGameAssets.MONSTER_ANIMATION);
        this.monsterSpine.position.set(360, 360);
        this.monsterSpine.state.setAnimation(0, "idle", true);
        if(this.boardAllowed) {
            this.monsterSpine.state.setAnimation(1, "board", true);
        }

        this.tvSpine = DecadenceGameAssets.createSpine(DecadenceGameAssets.TV_ANIMATION);
        this.tvSpine.position.set(360, 360);

        const allowedFeatures = Decadence.apiPlugin.betFeatureController.getAllowedFeatures();
        const linkedWinsPossible =  allowedFeatures.find(f => f.name.includes("REELS_2") || f.name.includes("REELS_4")) != undefined;


        this.linkedWinsSpine = DecadenceGameAssets.createSpine(DecadenceGameAssets.LINKED_WINS_ANIMATION);
        this.linkedWinsSpine.state.setAnimation(0, "off", true);
        this.linkedWinsSpine.position.set(360, 360);
        this.linkedWinsSpine.visible = linkedWinsPossible;

        this.lockedReelSpine = DecadenceGameAssets.createSpine(DecadenceGameAssets.GRID_ANIMATION);
        this.lockedReelSpine.position.set(360, 360);

        this.lockedReelMode1Positions = [new PIXI.Point(360, 195)];

        this.lockedReelMode2Positions = [
            new PIXI.Point(360, 195 - 30),
            new PIXI.Point(360, 195 + 60)
        ];

        this.lockedReelMode4Positions = [
            new PIXI.Point(360 - halfSymbolWidth, 195 - 35),
            new PIXI.Point(360 - halfSymbolWidth, 195 + 60),
            new PIXI.Point(360 + halfSymbolWidth, 195 - 35),
            new PIXI.Point(360 + halfSymbolWidth, 195 + 60)
        ]

        this.lockedReelContainer.name = "LockedReelContainer";
        for(let i = 0; i < 4; i++) {
            const sym = new DecadenceSymbol("0", i, 0);
            this.lockedSymbols.push(sym);
        }
        this.lockedReelContainer.addChild(...this.lockedSymbols);
        this.lockedReelContainer.addChild(this.lockedReelSpine);
        this.lockedReelContainer.alpha = 0;

        this.addChild(this.tvSpine);
        this.addChild(this.monsterSpine);
        this.addChild(this.lockedReelContainer);
        this.addChild(this.reelModels[3]);
        this.addChild(this.reelModels[2]);
        this.addChild(this.reelModels[1]);
        this.addChild(this.reelModels[0]);
        this.addChild(this.linkedWinsSpine);

        this.monsterWinSounds = new LoopableList(['voiceWin1', 'voiceWin2', 'voiceWin3']);
        this.monsterWinSounds.setNoDuplicates().setRandomizeAtWrapAround(true);

        this.monsterBigWinSounds = new LoopableList<string>(['voiceBigwin']);
        //this.monsterBigWinSounds.setNoDuplicates().setRandomizeAtWrapAround(true);

        this.numberOfActiveReels = 1;
        this.set1ReelMode().progress(1);
        this.onResize();
    }

    public set1ReelMode():gsap.core.Timeline {
        this.reelModeTransitionTL.progress(1);
        const tl = gsap.timeline();
        const reelsBefore = this.numberOfActiveReels;
        this.numberOfActiveReels = 1;
        if(this.boardAllowed) {
            tl.add(() => {
                this.monsterSpine.skeleton.setSkinByName("10k");
            })
        }

        if(reelsBefore != 1) {
            const animName = `${reelsBefore}to1`;
            tl.add(SpineGSAPWrapper.createTimeline(this.tvSpine, 0, animName));
            tl.add(() => {
                this.linkedWinsSpine.state.setAnimation(0, "off", true);
            }, "<")
        }
        this.reelModels.forEach(reel => {
            tl.add(gsap.to(reel, {alpha: 0, duration: 0.1}), "<");
        })
        tl.add(() => {
            this.reelMode1Positions.forEach((pos, index) => {
                this.reelModels[index].position.copyFrom(pos);
               // this.reelModels[index].scale.set(this.reelMode1Scale, this.reelMode1Scale);
                DecadenceStatics.SYMBOL_WIDTH = 379;
                DecadenceStatics.SYMBOL_HEIGHT = 230;
                this.reelModels[index].stepSize = DecadenceStatics.SYMBOL_HEIGHT;
                this.reelModels[index].setMask(1);
                this.reelModels[index].symbols[0].reInitialize();

                this.lockedSymbols[index].position.copyFrom(this.lockedReelMode1Positions[index]);
                this.lockedSymbols[index].reInitialize();
            })
            this.reelModels.forEach((s, index) => {
                s.visible = index < this.numberOfActiveReels;
                this.lockedSymbols[index].visible = s.visible;
            })
            const sortedReelValues = this.reelModels.map(s => s.symbols[0].name).sort();
            this.reelModels[0].symbols[0].changeSymbol(sortedReelValues[sortedReelValues.length-1]);
            this.reelModels[1].symbols[0].changeSymbol(sortedReelValues[0]);
            this.reelModels[2].symbols[0].changeSymbol(sortedReelValues[1]);
            this.reelModels[3].symbols[0].changeSymbol(sortedReelValues[2]);

            this.tvSpine.state.setAnimation(0, "1", true);
        })
        for(let i = 0; i < 1; i++) {
            tl.add(gsap.to(this.reelModels[i], {alpha: 1, duration: 0.1}), "<");
        }
        this.reelModeTransitionTL = tl;
        return tl;
    }
    public set2ReelMode():gsap.core.Timeline {
        this.reelModeTransitionTL.progress(1);
        const tl = gsap.timeline();
        const reelsBefore = this.numberOfActiveReels;
        this.numberOfActiveReels = 2;
        if(this.boardAllowed) {
            tl.add(() => {
                this.monsterSpine.skeleton.setSkinByName("20k");
            })
        }

        if(reelsBefore != 2) {
            const animName = `${reelsBefore}to2`;
            tl.add(SpineGSAPWrapper.createTimeline(this.tvSpine, 0, animName));
            tl.add(() => {
                this.linkedWinsSpine.state.setAnimation(0, "on", true);
            }, "<")
        }
        this.reelModels.forEach(reel => {
            tl.add(gsap.to(reel, {alpha: 0, duration: 0.1}), "<");
        })
        tl.add(() => {
            this.reelMode2Positions.forEach((pos, index) => {
                this.reelModels[index].position.copyFrom(pos);
                //this.reelModels[index].scale.set(this.reelMode2Scale, this.reelMode2Scale);
                DecadenceStatics.SYMBOL_WIDTH = 300;
                DecadenceStatics.SYMBOL_HEIGHT = 111;
                this.reelModels[index].stepSize = DecadenceStatics.SYMBOL_HEIGHT;
                this.reelModels[index].setMask(2);
                this.reelModels[index].getSymbols()[0].reInitialize();

                this.lockedSymbols[index].position.copyFrom(this.lockedReelMode2Positions[index]);
                this.lockedSymbols[index].reInitialize();
            })
            this.reelModels.forEach((s, index) => {
                s.visible = s.reelID < this.numberOfActiveReels;
                this.lockedSymbols[index].visible = s.visible;
            })
            const sortedReelValues = this.reelModels.map(s => s.symbols[0].name).sort();
            this.reelModels[0].symbols[0].changeSymbol(sortedReelValues[2]);
            this.reelModels[1].symbols[0].changeSymbol(sortedReelValues[3]);
            this.reelModels[2].symbols[0].changeSymbol(sortedReelValues[0]);
            this.reelModels[3].symbols[0].changeSymbol(sortedReelValues[1]);

            this.tvSpine.state.setAnimation(0, "2", true);
        })
        for(let i = 0; i < 2; i++) {
            tl.add(gsap.to(this.reelModels[i], {alpha: 1, duration: 0.1}), "<");
        }
        this.reelModeTransitionTL = tl;
        return tl;
    }
    public set4ReelMode():gsap.core.Timeline {
        this.reelModeTransitionTL.progress(1);
        const reelsBefore = this.numberOfActiveReels;
        this.numberOfActiveReels = 4;
        const tl = gsap.timeline();
        if(this.boardAllowed) {
            tl.add(() => {
                this.monsterSpine.skeleton.setSkinByName("40k");
            })
        }

        if(reelsBefore != 4) {
            const animName = `${reelsBefore}to4`;
            tl.add(SpineGSAPWrapper.createTimeline(this.tvSpine, 0, animName));
            tl.add(() => {
                this.linkedWinsSpine.state.setAnimation(0, "on", true);
            }, "<")
        }
        this.reelModels.forEach(reel => {
            tl.add(gsap.to(reel, {alpha: 0, duration: 0.1}), "<");
        })
        tl.add(() => {
            this.reelMode4Positions.forEach((pos, index) => {
                this.reelModels[index].position.copyFrom(pos);
                //this.reelModels[index].scale.set(this.reelMode4Scale, this.reelMode4Scale);
                DecadenceStatics.SYMBOL_WIDTH = 175;
                DecadenceStatics.SYMBOL_HEIGHT = 111;
                this.reelModels[index].stepSize = DecadenceStatics.SYMBOL_HEIGHT;
                this.reelModels[index].setMask(4);
                this.reelModels[index].getSymbols()[0].reInitialize();

                this.lockedSymbols[index].position.copyFrom(this.lockedReelMode4Positions[index]);
                this.lockedSymbols[index].reInitialize();
            })
            this.reelModels.forEach((s, index) => {
                s.visible = index < this.numberOfActiveReels;
                this.lockedSymbols[index].visible = s.visible;
            })
            const sortedReelValues = this.reelModels.map(s => s.symbols[0].name).sort();
            this.reelModels.forEach((reel, index) => {
                reel.symbols[0].changeSymbol(sortedReelValues[index]);
            })

            this.tvSpine.state.setAnimation(0, "4", true);
        })
        for(let i = 0; i < 4; i++) {
            tl.add(gsap.to(this.reelModels[i], {alpha: 1, duration: 0.1}), "<");
        }
        this.reelModeTransitionTL = tl;
        return tl;
    }

    public spinActiveReels(reelOffset:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {this.reelModeTransitionTL.progress(1)})
        for(let i = 0; i < this.numberOfActiveReels; i++) {
            const reelTL = Decadence.reelView.reelModels[i].start();
            tl.add(reelTL, i * reelOffset);
            if(i == 0) {
                tl.add(() => {
                    Decadence.soundPlayer.playLoopingSound("reelsSpinning")
                }, "<");
            }
        }
        return tl;
    }

    public setReelSetFromMode(mode:string) {
        this.reelModels.forEach(reel => reel.reelSet = DecadenceStatics.getReelSetFromMode(mode));
    }

    public resetNonActiveReelSymbols() {
        this.reelModels.slice(this.numberOfActiveReels).forEach(reel => {
            reel.symbols[0].changeSymbol("0");
        })
    }

    public stopActiveReels(reelOffset:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        if(this.numberOfActiveReels != Decadence.model.parsedReels.length) {
            console.log("ERROR; Number of active reels mismatch!");
        }

        let accumulatedWin = Decadence.model.accumulatedWin - Decadence.model.data.totalSpinWinnings;

        for(let reelID = 0; reelID < this.numberOfActiveReels; reelID++) {
            const reel = this.reelModels[reelID];
            const reelStopTL = reel.stop(Decadence.model.parsedReels[reelID].symbol);
            const singleReel = this.numberOfActiveReels == 1;

            tl.add(reelStopTL, singleReel ? reelOffset : reelID * reelOffset);
            tl.add(() => {
                reel.stopSymbol?.getLandAnimation();
                const landingSym = Decadence.model.parsedReels[reelID].symbol;
                const landedScatter = landingSym == "SC";
                const winOnReel = (landingSym != "0" && !landedScatter);
                Decadence.soundPlayer.playSound("reelLanding");

                if(winOnReel) {
                    accumulatedWin += (Number(landingSym) * Decadence.model.data.playedBetValue);
                    if(accumulatedWin > Decadence.model.betPrice || !Decadence.apiPlugin.gameClientConfiguration.belowStakeWinRestriction) {
                        Decadence.soundPlayer.playSound("reelLandingWin");
                    }
                }
                if(landedScatter && !Decadence.model.isFreeSpins) {
                    Decadence.soundPlayer.playSound("scatterLanding");
                } else if(landedScatter && Decadence.model.isFreeSpins) {
                    Decadence.soundPlayer.playSound("scatterLandingSuper");
                }
            })
            if(reelID == this.numberOfActiveReels-1) {
                tl.add(() => {Decadence.soundPlayer.stopSound("reelsSpinning")});
            }
        }
        return tl;
    }

    public clearLockedReels():gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(this.lockedSymbols.map(s => s.changeSymbol("0")));
        return tl;
    }

    public activeteLockedReels():gsap.core.Timeline {
        const tl = gsap.timeline();
        const activeAnim = `${this.numberOfActiveReels}` + "_activate";
        const idleAnim = `${this.numberOfActiveReels}` + "_idle";
        tl.add(() => {
            this.lockedReelContainer.alpha = 1;
            this.lockedSymbols.forEach(sym => sym.changeSymbol("0"));
            Decadence.soundPlayer.playSound("unlockSfsReels");
        })
        tl.add(SpineGSAPWrapper.createTimeline(this.lockedReelSpine, 0, activeAnim))
        tl.add(SpineGSAPWrapper.createTimeline(this.monsterSpine, 0, "fs_grid"))
        tl.add(() => {
            this.lockedReelSpine.state.setAnimation(0, idleAnim, true);
            this.monsterSpine.state.setAnimation(0, "idle_awake", true);
        })
        return tl;
    }

    public playMonsterWakeUp():gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(SpineGSAPWrapper.createTimeline(this.monsterSpine, 0, "fs_intro"))
        if(this.boardAllowed) {
            tl.add(SpineGSAPWrapper.createTimeline(this.monsterSpine, 1, "board_fs_intro"), "<");
            tl.add(() => {
                // Remove sticker on board in case we are playing in boosted bet
                if(this.boardAllowed) {
                    this.monsterSpine.skeleton.setSkinByName("10k");
                }
            })
        }
        tl.add(() => {
            this.monsterSpine.state.setAnimation(0, "idle_awake", true);
        })
        return tl;
    }

    public playMonsterScreamSFS():gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(SpineGSAPWrapper.createTimeline(this.monsterSpine, 0, "fs_intro"));
        tl.add(() => {
            this.monsterSpine.state.setAnimation(0, "idle_awake", true);
        })
        return tl;
    }

    public playMonsterFallAsleep():gsap.core.Timeline {
        const tl = gsap.timeline();
        if(this.boardAllowed) {
            // Show sticker on board in case we have more than 1 active reel
            if(this.numberOfActiveReels > 1) {
                switch (this.numberOfActiveReels) {
                    case 2:
                        this.monsterSpine.skeleton.setSkinByName("20k");
                        break;
                    case 4:
                        this.monsterSpine.skeleton.setSkinByName("40k");
                        break;
                    default:
                        break;
                }
            }
            tl.add(SpineGSAPWrapper.createTimeline(this.monsterSpine, 1, "board_fs_outro"));
        }
        tl.add(SpineGSAPWrapper.createTimeline(this.monsterSpine, 0, "fs_outro"), "<")
        tl.add(gsap.to(this.lockedReelContainer, {alpha: 0, duration: 0.5}), "<")
        tl.add(() => {
            this.monsterSpine.state.setAnimation(0, "idle", true);
            if(this.boardAllowed) {
                this.monsterSpine.state.setAnimation(1, "board", true);
            }
        })
        return tl;
    }

    public playMonsterWin():gsap.core.Timeline {
        const tl = gsap.timeline();
        const winAnim:string = Decadence.model.isFreeSpins ? "win_awake" : "win";
        tl.add(() => {
            Decadence.soundPlayer.playSound(this.monsterWinSounds.next());
        })
        tl.add(SpineGSAPWrapper.createTimeline(this.monsterSpine, 0, winAnim))
        tl.add(() => {
            const idleAnim:string = Decadence.model.isFreeSpins ? "idle_awake" : "idle";
            this.monsterSpine.state.setAnimation(0, idleAnim, true);
        })
        return tl;
    }

    public playMonsterBigWin():gsap.core.Timeline {
        const tl = gsap.timeline();
        const winAnim:string = Decadence.model.isFreeSpins ? "win_big_awake" : "win_big";
        tl.add(() => {
            Decadence.soundPlayer.playSound(this.monsterBigWinSounds.next());
        })
        tl.add(SpineGSAPWrapper.createTimeline(this.monsterSpine, 0, winAnim));
        tl.add(() => {
            const idleAnim:string = Decadence.model.isFreeSpins ? "idle_awake" : "idle";
            this.monsterSpine.state.setAnimation(0, idleAnim, true);
        })
        return tl;
    }

    public getSymbolAt(reelID:number):DecadenceSymbol {
        return this.reelModels[reelID].stopSymbol;
    }

    public getActiveSymbols():DecadenceSymbol[] {
        const symbols:DecadenceSymbol[] = [];
        for(let i = 0; i < this.numberOfActiveReels; i++) {
            symbols.push(this.reelModels[i].stopSymbol);
        }
        return symbols;
    }

    public onRestore():void {
        const numberOfActiveReels = Decadence.model.initData.reels.length;
        switch (numberOfActiveReels) {
            case 1: this.set1ReelMode().progress(1); break;
            case 2: this.set2ReelMode().progress(1); break;
            case 4: this.set4ReelMode().progress(1); break;
        }

        for(let i = 0; i < this.numberOfActiveReels; i++) {
            this.reelModels[i].symbols[0].changeSymbol(Decadence.model.initData.reels[i]);
            this.reelModels[i].stopSymbol = new DecadenceSymbol(Decadence.model.initData.reels[i], i, 0);
        }

        this.setReelSetFromMode(Decadence.model.initData.mode);

        if(Decadence.model.isFreeSpins || Decadence.model.triggeredFreeSpins) {
            this.monsterSpine.state.setAnimation(0, "idle_awake", true);
            if(this.boardAllowed) {
                this.monsterSpine.state.setEmptyAnimation(1, 0);
            }

            if(this.boardAllowed) {
                this.monsterSpine.skeleton.setSkinByName("10k");
            }
        }
        if(Decadence.model.isSuperFreeSpins || Decadence.model.nextMode == "FREESPIN_SUPER") {
            this.activeteLockedReels().progress(1);

            Decadence.model.initData.highestPayingWins.forEach((win, index) => {
                this.lockedSymbols[index].changeSymbol(win);
            });
        }
    }

    onResize() {
        const isPortrait = !NolimitApplication.isLandscape;
        if(isPortrait) {
            this.position.y = DecadenceStatics.GAME_ELEMENTS_PORTRAIT_OFFSET;
        } else {
            this.position.y = 0;
        }
    }
}